<template>
  <app-table
    v-if="isLoaded"
    primary-key="upperLimitItemName"
    :fields="limitTb.fields"
    :items="limitTb.items"
    :per-page="-1"
    :bordered="true"
    :no-data-text="$filter.message('no_data')"
    :tr-class="() => undefined"
  >
    <template #cell(current)="{ item }">
      <template v-if="item.current == null">-</template>
      <template v-else>{{ item.current }} {{ item.unit }}</template>
    </template>
    <template #cell(upperLimit)="{ item }">
      {{ item.upperLimit }} {{ item.unit }}
    </template>
  </app-table>
</template>

<script lang="ts">
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { ApiNotFoundError } from "@/error/errors";

export default defineComponent({
  name: "UpperLimit",
  props: {
    /** 上限値管理単位 */
    upperLimitManageUnit: {
      type: String as PropType<
        | "ACCESS"
        | "PFGW"
        | "VNL2L3"
        | "VN"
        | "APPLIANCE"
        | "MULTI_CLOUD"
        | "SASE_GATEWAY"
      >,
      required: true,
    },
    /** E番号 (upperLimitManageUnit=ACCESS, VNL2L3 の場合は必須) */
    enumber: {
      type: String as PropType<string>,
      required: false,
    },
    /** W番号 (upperLimitManageUnit=VN, MULTI_CLOUD, SASE_GATEWAY の場合は必須) */
    wnumber: {
      type: String as PropType<string>,
      required: false,
    },
    /** クラウド回線SEQ (上限値管理単位がマルチクラウド接続の場合は必須) */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: false,
    },
    /** SASEゲートウェイ種別 (上限値管理単位がSASEゲートウェイかつSASEゲートウェイのVNコネクトが存在する場合は必須) */
    saseGatewayServiceType: {
      type: String as PropType<"PALOALTO">,
      required: false,
    },
  },
  data() {
    return {
      isLoaded: false,
      limitTb: {
        fields: [
          { key: "upperLimitItemName", title: "設定項目" },
          { key: "current", title: "現在設定数" },
          { key: "upperLimit", title: "上限値" },
        ],
        items: [] as UpperLimitEntity[],
      },
    };
  },
  async mounted() {
    try {
      const limit = await this.$api.upperLimit.getUpperLimitLine({
        upperLimitLineManageUnit: this.upperLimitManageUnit,
        enumber: this.enumber,
        wnumber: this.wnumber,
        cloudLineSeq: this.cloudLineSeq,
        saseGatewayServiceType: this.saseGatewayServiceType,
      });

      switch (this.upperLimitManageUnit) {
        /** アクセス回線情報画面・IPsec情報画面 */
        case "ACCESS":
          this.limitTb.items = limit.accessLine!.upperLimitList;
          break;
        /** VNコネクト情報画面 */
        case "VN":
          this.limitTb.items = limit.vnConnect!.upperLimitList;
          break;
        /** VNL2/L3コネクト情報画面 */
        case "VNL2L3":
          this.limitTb.items = limit.vnl2l3Line!.upperLimitList;
          break;
        /** マルチクラウド情報画面 */
        case "MULTI_CLOUD":
          this.limitTb.items = limit.cloudLine!.upperLimitList;
          break;
        /** SASEゲートウェイ情報画面 */
        case "SASE_GATEWAY":
          this.limitTb.items = limit.saseGateway!.upperLimitList;
          break;
      }
    } catch (e) {
      // 回線によっては上限値がそもそも存在しない場合がある
      // その際は404が返ってくるため上限値0件として表示する
      if (e instanceof ApiNotFoundError) {
        this.limitTb.items = [];
        return;
      }
      throw e;
    } finally {
      this.isLoaded = true;
    }
  },
});
</script>
